import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { Bell, BellOff, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const canUsePush = () =>
        typeof window !== "undefined" &&
        "Notification" in window &&
        "serviceWorker" in navigator &&
        "PushManager" in window &&
        window.isSecureContext;

const urlBase64ToUint8Array = (base64String) => {
        const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
        const base64 = `${base64String}${padding}`.replace(/-/g, "+").replace(/_/g, "/");
        const rawData = window.atob(base64);
        const outputArray = new Uint8Array(rawData.length);

        for (let i = 0; i < rawData.length; i += 1) {
                outputArray[i] = rawData.charCodeAt(i);
        }
        return outputArray;
};

export function NotificationPrompt() {
        const location = useLocation();
        const [config, setConfig] = useState(null);
        const [subscription, setSubscription] = useState(null);
        const [loading, setLoading] = useState(false);
        const [ready, setReady] = useState(false);

        const hiddenByRoute = useMemo(
                () => location.pathname.startsWith("/admin"),
                [location.pathname]
        );

        useEffect(() => {
                let mounted = true;

                const load = async () => {
                        if (hiddenByRoute || !canUsePush() || Notification.permission === "denied") {
                                return;
                        }
                        try {
                                const response = await api.get("/push/config");
                                if (!mounted || !response.data?.configured || !response.data?.public_key) {
                                        return;
                                }
                                const registration = await navigator.serviceWorker.register("/push-sw.js");
                                const existing = await registration.pushManager.getSubscription();
                                if (!mounted) return;
                                setConfig(response.data);
                                setSubscription(existing);
                                setReady(true);
                        } catch {
                                if (mounted) setReady(false);
                        }
                };

                load();
                return () => {
                        mounted = false;
                };
        }, [hiddenByRoute]);

        const subscribe = async () => {
                if (!config?.public_key) return;
                setLoading(true);
                try {
                        const permission = await Notification.requestPermission();
                        if (permission !== "granted") {
                                toast.info("As notificações não foram activadas neste browser.");
                                return;
                        }
                        const registration = await navigator.serviceWorker.register("/push-sw.js");
                        const existing = await registration.pushManager.getSubscription();
                        const activeSubscription =
                                existing ||
                                (await registration.pushManager.subscribe({
                                        userVisibleOnly: true,
                                        applicationServerKey: urlBase64ToUint8Array(config.public_key),
                                }));
                        await api.post("/push/subscribe", activeSubscription.toJSON());
                        setSubscription(activeSubscription);
                        toast.success("Notificações activas.");
                } catch (error) {
                        toast.error(
                                error?.response?.data?.detail ||
                                        "Não foi possível activar notificações."
                        );
                } finally {
                        setLoading(false);
                }
        };

        const unsubscribe = async () => {
                setLoading(true);
                try {
                        const registration = await navigator.serviceWorker.getRegistration();
                        const existing = subscription || (await registration?.pushManager.getSubscription());
                        if (existing) {
                                await api.post("/push/unsubscribe", { endpoint: existing.endpoint });
                                await existing.unsubscribe();
                        }
                        setSubscription(null);
                        toast.success("Notificações desactivadas.");
                } catch {
                        toast.error("Não foi possível desactivar notificações.");
                } finally {
                        setLoading(false);
                }
        };

        if (!ready || hiddenByRoute) return null;

        return (
                <div className="fixed left-3 bottom-20 z-[90] sm:bottom-4">
                        {subscription ? (
                                <div className="flex items-center gap-2 border border-[var(--line-strong)] bg-[var(--paper-2)] px-3 py-2 shadow-lg">
                                        <span className="overline text-[10px] text-[var(--ink-soft)]">
                                                Notificações activas
                                        </span>
                                        <button
                                                type="button"
                                                onClick={unsubscribe}
                                                disabled={loading}
                                                className="inline-flex h-9 w-9 items-center justify-center border border-[var(--line)] text-[var(--ink-soft)] hover:bg-[var(--ink)] hover:text-[var(--bg-deep)] disabled:opacity-50"
                                                aria-label="Desactivar notificações"
                                        >
                                                {loading ? <Loader2 size={15} className="animate-spin" /> : <BellOff size={15} />}
                                        </button>
                                </div>
                        ) : (
                                <button
                                        type="button"
                                        onClick={subscribe}
                                        disabled={loading}
                                        className="inline-flex items-center gap-2 border border-[var(--line-strong)] bg-[var(--paper-2)] px-3 py-2 text-left shadow-lg hover:bg-[var(--ink)] hover:text-[var(--bg-deep)] disabled:opacity-50"
                                >
                                        {loading ? <Loader2 size={15} className="animate-spin" /> : <Bell size={15} />}
                                        <span className="overline text-[10px]">Receber novos dossiês</span>
                                </button>
                        )}
                </div>
        );
}
